// CatalogBlock — places the catalog browser in a slot.
//
// Two variants:
//   "grid" (default) — the existing CatalogPanel with breadcrumb navigation
//      and card grid. Used by the standard layout.
//   "editorial-row" — flat list of rows (image + name + spec subtitle).
//      Subcategories render as plain serif headings to group their rows.
//      Used by single-menu and other editorial layouts.
//
// `specParameterKeys` is an ordered list of parameter-definition keys whose
// values get joined with " / " into the row's spec subtitle. The keys come
// from each product's `parameterValues` map on the wire (populated by the
// embed endpoint from DProductParameterValueService). Missing values are
// skipped, so a product without one of the listed keys just renders a
// shorter spec line.

import { useEffect, useMemo, useRef, type MouseEvent } from "react";
import { CatalogPanel } from "../components/CatalogPanel";
import { useIsTouch } from "../useIsTouch";
import type { SceneCategoryDto, SceneProductDto } from "../types";
import type { BlockHostContext, BlockUniversalProps } from "./types";

export interface CatalogBlockProps extends BlockUniversalProps {
  host: BlockHostContext;
  variant?: "grid" | "editorial-row";
  /** Ordered parameter-definition keys whose values fill the spec subtitle.
   *  Empty / missing = no subtitle. */
  specParameterKeys?: string[];
}

export function CatalogBlock({
  themeVariant,
  className,
  host,
  variant = "grid",
  specParameterKeys = [],
}: CatalogBlockProps) {
  if (variant === "editorial-row") {
    return (
      <EditorialRowCatalog
        host={host}
        specParameterKeys={specParameterKeys}
        className={className}
        themeVariant={themeVariant}
      />
    );
  }
  // Grid is the existing CatalogPanel; this wrapper just provides a slot-
  // placeable container so the block dispatcher can render it like the others.
  return (
    <div
      className={"sd-block-catalog" + (className ? " " + className : "")}
      data-variant={themeVariant}
    >
      <CatalogPanel
        categories={host.visibleCategories}
        products={host.products}
        isOpen
        onRequestClose={undefined}
      />
    </div>
  );
}

// ── Editorial-row catalog ─────────────────────────────────────────────────
// Flat list of rows. Subcategories appear as plain serif headings; products
// underneath are draggable. The single-pass walk preserves the catalog tree's
// natural ordering — subcategories rendered in their parent's order, with
// their own products and any sub-subcategories nested below.

function EditorialRowCatalog({
  host,
  specParameterKeys,
  className,
  themeVariant,
}: {
  host: BlockHostContext;
  specParameterKeys: string[];
  className?: string;
  themeVariant?: string;
}) {
  // Build a parent → children index of categories once per render. Cheap;
  // catalogs are at most a few hundred items.
  const childrenByParent = useMemo(() => {
    const m = new Map<number | null, SceneCategoryDto[]>();
    for (const c of host.visibleCategories) {
      const key = c.parentId;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(c);
    }
    for (const list of m.values()) list.sort((a, b) => a.sortOrder - b.sortOrder);
    return m;
  }, [host.visibleCategories]);

  const productById = useMemo(() => {
    const m = new Map<string, SceneProductDto>();
    for (const p of host.products) m.set(p.id, p);
    return m;
  }, [host.products]);

  // Identify the roots — categories whose parent isn't in the visible set, or
  // categories with parentId == null. Walking from there visits everything in
  // scope without rendering anything outside the active series.
  const visibleIds = useMemo(
    () => new Set(host.visibleCategories.map(c => c.id)),
    [host.visibleCategories]
  );
  const roots = useMemo(
    () => host.visibleCategories
      .filter(c => c.parentId == null || !visibleIds.has(c.parentId))
      .sort((a, b) => a.sortOrder - b.sortOrder),
    [host.visibleCategories, visibleIds]
  );

  // If there are no roots but there are products (single-series flat case),
  // render the products bare.
  const hasAnyCategory = host.visibleCategories.length > 0;

  // Clicking/tapping a product row without dragging it in does nothing on its
  // own — placing is drag-only. We surface a hint through the notification
  // system (host.notify) rather than rendering our own banner, so the message
  // is authorable (a notification block in the `notifications` slot) and shows
  // as a top-of-screen toast over the scrollview. Touch vs. mouse fire
  // different triggers so each can carry its own copy ("use the handle" vs.
  // "drag to place"). Taps on the drag handle itself are real drags — ignored.
  const isTouch = useIsTouch();

  // Distinguish a genuine tap (fire the hint) from a drag that happened to end
  // with a trailing click event (suppress it). A real drag moves the pointer;
  // a tap doesn't. We record the pointerdown position and only fire the hint
  // when the click landed within DRAG_SLOP pixels of where the press started.
  // Without this the hint mis-fired on every drag that released near its origin
  // (the browser still emits a click after pointerdown→move→pointerup).
  //
  // The press position is captured on a document capture-phase listener, not a
  // React onPointerDown: the drag is owned by configurator-viewer.js, which on
  // pointerdown over a [data-product-id] element calls preventDefault/
  // stopPropagation — a bubbling React handler could be cut off before it runs.
  // Capture phase fires before any of that.
  const downPos = useRef<{ x: number; y: number } | null>(null);
  const DRAG_SLOP = 6; // px; matches the typical browser click-vs-drag threshold

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      downPos.current = { x: e.clientX, y: e.clientY };
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, []);

  const onCatalogClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest(".sd-block-catalog__row-drag")) return; // genuine drag affordance
    if (!target.closest(".sd-block-catalog__row")) return;     // not a product row

    // Suppress the hint when the pointer moved past the slop — that was a drag,
    // not a tap. Missing start position (e.g. keyboard-driven click) counts as
    // no movement, so the hint still fires for a real activation.
    const start = downPos.current;
    downPos.current = null;
    if (start) {
      const moved = Math.hypot(e.clientX - start.x, e.clientY - start.y);
      if (moved > DRAG_SLOP) return;
    }
    host.notify?.(isTouch ? "product-tap-no-drag" : "product-click-no-drag");
  };

  return (
    <div
      className={"sd-block-catalog sd-block-catalog--editorial-row" + (className ? " " + className : "")}
      data-variant={themeVariant}
      onClick={onCatalogClick}
    >
      {hasAnyCategory
        ? roots.map(root => (
            <CategoryGroup
              key={root.id}
              category={root}
              childrenByParent={childrenByParent}
              productById={productById}
              specParameterKeys={specParameterKeys}
              collectionPicks={host.collectionPicks}
              depth={0}
            />
          ))
        : host.products.map(p => (
            <ProductRow
              key={p.id}
              product={p}
              specParameterKeys={specParameterKeys}
              collectionPicks={host.collectionPicks}
            />
          ))}
    </div>
  );
}

function CategoryGroup({
  category,
  childrenByParent,
  productById,
  specParameterKeys,
  collectionPicks,
  depth,
}: {
  category: SceneCategoryDto;
  childrenByParent: Map<number | null, SceneCategoryDto[]>;
  productById: Map<string, SceneProductDto>;
  specParameterKeys: string[];
  collectionPicks: Record<string, { materialId: string; dmatAssetId: string | null }>;
  depth: number;
}) {
  const subs = childrenByParent.get(category.id) ?? [];
  const products = category.productIds
    .map(pid => productById.get(pid))
    .filter((p): p is SceneProductDto => !!p);

  return (
    <section className="sd-block-catalog__group" data-depth={depth}>
      {/* Root group's name is rendered by the slot's text block above the
          catalog, so we only emit a heading for non-root subcategories. The
          design uses plain serif headings without icons (per user note). */}
      {depth > 0 && (
        <h6 className="sd-block-catalog__group-heading">{category.name}</h6>
      )}
      {products.length > 0 && (
        <ul className="sd-block-catalog__rows">
          {products.map(p => (
            <li key={p.id}>
              <ProductRow product={p} specParameterKeys={specParameterKeys} collectionPicks={collectionPicks} />
            </li>
          ))}
        </ul>
      )}
      {subs.map(sub => (
        <CategoryGroup
          key={sub.id}
          category={sub}
          childrenByParent={childrenByParent}
          productById={productById}
          specParameterKeys={specParameterKeys}
          collectionPicks={collectionPicks}
          depth={depth + 1}
        />
      ))}
    </section>
  );
}

function ProductRow({
  product,
  specParameterKeys,
  collectionPicks,
}: {
  product: SceneProductDto;
  specParameterKeys: string[];
  collectionPicks: Record<string, { materialId: string; dmatAssetId: string | null }>;
}) {
  // Spec subtitle — pull each requested key from the product's values, drop
  // missing ones, join with " / ". If nothing resolves, the subtitle is empty
  // and the row renders a single line. (Layout CSS handles the spacing.)
  const spec = useMemo(() => {
    const values = product.parameterValues;
    if (!values || specParameterKeys.length === 0) return "";
    const parts: string[] = [];
    for (const key of specParameterKeys) {
      const v = values[key];
      if (v != null && v !== "") parts.push(v);
    }
    return parts.join(" / ");
  }, [product.parameterValues, specParameterKeys]);

  // Resolve the user's left-panel collection picks into per-surface spawn
  // bindings for this product. Walks the product's surfaceCollections map
  // (surface key → collection id) and pulls the picked DMatAssetId from
  // collectionPicks. Surfaces whose collection has no pick yet, and picks
  // whose material has no DMatAssetId on the wire, are skipped — the spawn-
  // bundle's server-resolved default still applies to those. The JS drag
  // bridge reads this off the row's data attribute at DragStart and forwards
  // to Unity as `bindings`.
  const spawnBindings = useMemo(() => {
    const surfaceMap = product.surfaceCollections;
    if (!surfaceMap) return "";
    const out: { Key: string; DmatId: string }[] = [];
    for (const [surfaceKey, collectionId] of Object.entries(surfaceMap)) {
      const pick = collectionPicks[collectionId];
      if (!pick?.dmatAssetId) continue;
      out.push({ Key: surfaceKey, DmatId: pick.dmatAssetId });
    }
    return out.length > 0 ? JSON.stringify(out) : "";
  }, [product.surfaceCollections, collectionPicks]);

  // Touch vs. non-touch decides the drag affordance (NOT the layout breakpoint):
  //  - Non-touch (mouse): the whole row is the drag source — pressing anywhere
  //    starts a drag, no handle shown. There's no touch-scroll to fight.
  //  - Touch: dragging the whole row would hijack touch-scroll, so only a
  //    dedicated handle on the right carries [data-product-id]; the rest of the
  //    row scrolls freely. A touch tablet on the desktop layout still gets the
  //    handle; a non-touch narrow window on the mobile layout still drags whole.
  // The drag JS finds [data-spawn-bindings] / .browser-item via .closest(), so
  // the row keeps both regardless and works as the handle's drag ancestor.
  const isTouch = useIsTouch();

  return (
    // [data-product-id] is the contract with configurator-viewer.js's drag
    // listener — any element with this attribute starts a drag on pointerdown.
    // browser-item is what its ghost-clone code expects to find.
    // [data-spawn-bindings] is read by the drag bridge and forwarded to
    // Unity in DragStart so the spawn carries the user's chosen defaults.
    <div
      className={
        "sd-block-catalog__row browser-item" +
        (isTouch ? " sd-block-catalog__row--touch" : "")
      }
      data-track-key="catalog.product"
      data-product-id={isTouch ? undefined : product.id}
      data-spawn-bindings={spawnBindings || undefined}
      title={isTouch ? undefined : "Drag to place in scene"}
    >
      {/* Selection dot — idle: white with a hairline ring; on row hover it
          fills black. Pure affordance; styling lives in the layout CSS. */}
      <span className="sd-block-catalog__row-dot" aria-hidden="true" />
      <div className="sd-block-catalog__row-img">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={product.name} draggable={false} />
        ) : (
          <span className="sd-block-catalog__row-img-empty" aria-hidden="true" />
        )}
      </div>
      <div className="sd-block-catalog__row-text">
        <p className="sd-block-catalog__row-name">{product.name}</p>
        {spec && <p className="sd-block-catalog__row-spec">{spec}</p>}
      </div>
      {isTouch && (
        // Touch drag handle — the only element carrying [data-product-id] on
        // touch, so it's the sole drag source. Sits at the end of the row and
        // signals "drag me into the scene". The grip glyph is decorative.
        <div
          className="sd-block-catalog__row-drag"
          data-product-id={product.id}
          title="Drag to place in scene"
          aria-label={"Drag " + product.name + " into the scene"}
          role="button"
        >
          <span className="sd-block-catalog__row-drag-grip" aria-hidden="true">⠿</span>
        </div>
      )}
    </div>
  );
}
